// UserDetail — drill-down view of a single user with editable profile +
// subscription fields (PX-120). Backend already exposes GET/PATCH/DELETE
// at /admin/users/{id}; this page is the UI wrapper.
//
// Sections (in render order):
//   - Header: email, badges (active/inactive/deleted), apple_sub, created_at.
//   - Profile: full_name (inline edit) + is_active toggle (confirm on disable).
//   - Subscription: plan picker + credits + expires_at + clear-expires.
//   - Generation counts: read-only table.
//   - Danger zone: collapsible soft-delete with confirm.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { APIError, request } from '@/lib/api'
import { PlanTag } from '@/components/PlanPicker'
import { SubscriptionPicker, type SubscriptionChange } from '@/components/SubscriptionPicker'
import type {
  AdminDeviceResponse,
  AdminPlanResponse,
  AdminSubscriptionProductResponse,
  AdminUserDetail,
  AdminUserUpdate,
} from '@/types/api'

// ---------------------------------------------------------------------
// Date helpers

/** Format a backend ISO datetime for human reading (no seconds). */
function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/** Convert backend ISO `2026-05-29T12:34:56.789Z` → input[type=datetime-local]
 *  value `2026-05-29T12:34` in the BROWSER's local timezone. */
function isoToDatetimeLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  // 16 chars: YYYY-MM-DDTHH:MM (datetime-local doesn't accept timezone).
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Convert datetime-local input value → backend ISO with the user's
 *  current timezone offset preserved. Empty string → null. */
function datetimeLocalInputToISO(value: string): string | null {
  if (!value) return null
  // `new Date('2026-05-29T12:34')` parses in local time; toISOString() emits
  // UTC ISO that Pydantic accepts.
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

// ---------------------------------------------------------------------

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const detailQuery = useQuery({
    queryKey: ['admin', 'users', 'detail', userId],
    queryFn: () => request<AdminUserDetail>(`/admin/users/${userId}`),
    enabled: !!userId,
  })

  const plansQuery = useQuery({
    queryKey: ['admin', 'catalog', 'plans'],
    queryFn: () => request<AdminPlanResponse[]>('/admin/catalog/plans'),
    staleTime: 5 * 60_000,
  })

  // PX-204: subscription-product catalog feeds the SubscriptionPicker (Free +
  // VIP SKUs). Same 5-min cache as the plan catalog — both are admin-edited.
  const productsQuery = useQuery({
    queryKey: ['admin', 'catalog', 'subscription-products'],
    queryFn: () => request<AdminSubscriptionProductResponse[]>('/admin/catalog/subscription-products'),
    staleTime: 5 * 60_000,
  })

  const patchMutation = useMutation({
    mutationFn: (body: AdminUserUpdate) =>
      request<AdminUserDetail>(`/admin/users/${userId}`, { method: 'PATCH', body }),
    onSuccess: (next) => {
      queryClient.setQueryData(['admin', 'users', 'detail', userId], next)
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => request<unknown>(`/admin/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      navigate('/users')
    },
  })

  if (detailQuery.error instanceof APIError && detailQuery.error.status === 404) {
    return (
      <div className="p-6">
        <Link to="/users" className="text-sm text-brand-600 hover:underline">
          ← Back to users
        </Link>
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
          User <code className="font-mono">{userId}</code> not found.
        </div>
      </div>
    )
  }

  if (detailQuery.isLoading || !detailQuery.data) {
    return <div className="p-6 text-sm text-ink-400">Loading…</div>
  }

  const u = detailQuery.data
  const plans = plansQuery.data ?? []
  const products = productsQuery.data ?? []
  const currentPlan = plans.find((p) => p.id === u.subscription.plan_id) ?? null

  return (
    <div className="space-y-6 p-6">
      <Link to="/users" className="text-sm text-brand-600 hover:underline">
        ← Back to users
      </Link>

      {/* ============ Header ============ */}
      <header className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {u.email ?? <span className="text-ink-400">(no email)</span>}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-500">
              <code className="rounded bg-ink-100 px-1.5 py-0.5 text-[11px]">{u.id}</code>
              <span>created {formatDateTime(u.created_at)}</span>
              {u.apple_sub && (
                <span>· apple_sub <code className="font-mono">{u.apple_sub.slice(0, 16)}…</code></span>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {u.deleted_at ? (
              <Badge tone="red">Deleted {formatDateTime(u.deleted_at)}</Badge>
            ) : u.is_active ? (
              <Badge tone="green">Active</Badge>
            ) : (
              <Badge tone="amber">Inactive</Badge>
            )}
            <PlanTag plan={currentPlan} fallbackSlug={u.subscription.plan_slug} />
            {u.subscription.is_expired && (
              <span
                title={`Nominal ${u.subscription.plan_name} lapsed${
                  u.subscription.expires_at ? ` ${formatDateTime(u.subscription.expires_at)}` : ''
                } — the user is effectively on Free until the PX-206 expiry job / reconcile cron settles the row.`}
              >
                <Badge tone="amber">Expired → Free</Badge>
              </span>
            )}
          </div>
        </div>
      </header>

      {patchMutation.error instanceof APIError && (
        <ErrorBanner message={patchMutation.error.message} />
      )}
      {deleteMutation.error instanceof APIError && (
        <ErrorBanner message={deleteMutation.error.message} />
      )}

      {/* ============ Profile ============ */}
      <Section title="Profile">
        <ProfileForm
          detail={u}
          onSave={(patch) => patchMutation.mutate(patch)}
          saving={patchMutation.isPending}
        />
      </Section>

      {/* ============ Subscription ============ */}
      <Section title="Subscription">
        <SubscriptionForm
          detail={u}
          plans={plans}
          products={products}
          plansLoading={plansQuery.isLoading || productsQuery.isLoading}
          onSave={(patch) => patchMutation.mutate(patch)}
          saving={patchMutation.isPending}
        />
      </Section>

      {/* ============ Generation counts ============ */}
      <Section title="Generations">
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <CountTile label="Queued" count={u.generation_counts.queued} />
          <CountTile label="Processing" count={u.generation_counts.processing} />
          <CountTile label="Completed" count={u.generation_counts.completed} />
          <CountTile label="Failed" count={u.generation_counts.failed} />
        </dl>
        <p className="mt-3 text-xs text-ink-400">
          <Link to={`/generations?user_id=${u.id}`} className="text-brand-600 hover:underline">
            See full generation history →
          </Link>
        </p>
      </Section>

      {/* ============ Devices ============ */}
      <Section title="Devices">
        <DevicesList devices={u.devices} />
      </Section>

      {/* ============ Danger zone ============ */}
      {!u.deleted_at && (
        <DangerZone
          email={u.email ?? u.id}
          pending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
        />
      )}
    </div>
  )
}

// =====================================================================
// Reusable bits
// =====================================================================

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-ink-500">{title}</h2>
      {children}
    </section>
  )
}

function Badge({ tone, children }: { tone: 'green' | 'amber' | 'red'; children: React.ReactNode }) {
  const palette =
    tone === 'green' ? 'bg-emerald-100 text-emerald-700' :
    tone === 'amber' ? 'bg-amber-100 text-amber-800' :
    'bg-red-100 text-red-700'
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${palette}`}>{children}</span>
  )
}

function CountTile({ label, count }: { label: string; count: number }) {
  return (
    <div className="rounded-lg border border-ink-100 bg-ink-50 px-3 py-2">
      <dt className="text-xs uppercase tracking-wide text-ink-400">{label}</dt>
      <dd className="mt-0.5 text-xl font-semibold tabular-nums text-ink-900">{count}</dd>
    </div>
  )
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {message}
    </div>
  )
}

// =====================================================================
// Devices (PX-282 Трек 2 + PX-283 Трек 3) — read-only fingerprint list,
// most-recent first. `country` is filled by AppsFlyer conversion-data (Трек 3),
// shown as its own field.
// =====================================================================

function DevicesList({ devices }: { devices: AdminDeviceResponse[] }) {
  if (devices.length === 0) {
    return <p className="text-sm text-ink-400">No devices synced yet.</p>
  }
  return (
    <div className="space-y-3">
      {devices.map((d) => (
        <DeviceCard key={d.idfv} device={d} />
      ))}
    </div>
  )
}

function DeviceCard({ device: d }: { device: AdminDeviceResponse }) {
  const locale = d.locale || d.language || null
  return (
    <div className="rounded-lg border border-ink-100 bg-ink-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-ink-900">
          {d.model_display ?? <code className="font-mono">{d.model_raw}</code>}
          <span className="ml-2 text-xs font-normal text-ink-500">iOS {d.os_version}</span>
        </div>
        <span className="text-xs text-ink-400">Last seen {formatDateTime(d.last_seen_at)}</span>
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-ink-500 sm:grid-cols-3">
        <DeviceField label="App" value={`${d.app_version} (${d.app_build})`} />
        <DeviceField label="Locale" value={locale} />
        <DeviceField label="Country" value={d.country} />
        <DeviceField label="Timezone" value={d.timezone} />
        <DeviceField label="Network" value={d.connection_type} />
        <DeviceField label="First launch" value={d.first_launch_at ? formatDateTime(d.first_launch_at) : null} />
        <DeviceField label="IDFV" value={d.idfv} mono />
      </dl>
    </div>
  )
}

function DeviceField({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div className="flex flex-col">
      <dt className="uppercase tracking-wide text-ink-400">{label}</dt>
      <dd className={`text-ink-700 ${mono ? 'truncate font-mono text-[11px]' : ''}`}>{value ?? '—'}</dd>
    </div>
  )
}

// =====================================================================
// Profile form — full_name + is_active
// =====================================================================

function ProfileForm({
  detail,
  onSave,
  saving,
}: {
  detail: AdminUserDetail
  onSave: (patch: AdminUserUpdate) => void
  saving: boolean
}) {
  const [name, setName] = useState(detail.full_name ?? '')
  const [pendingDeactivate, setPendingDeactivate] = useState(false)

  // Re-sync local form when the server-side detail changes (after PATCH).
  useEffect(() => { setName(detail.full_name ?? '') }, [detail.full_name])

  const nameDirty = name !== (detail.full_name ?? '')

  const handleNameSave = () => {
    if (!nameDirty) return
    onSave({ full_name: name.trim() || null })
  }

  const handleActiveToggle = (next: boolean) => {
    if (!next && detail.is_active) {
      setPendingDeactivate(true)
      return
    }
    onSave({ is_active: next })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1">
        <label className="text-xs uppercase tracking-wide text-ink-400">Full name</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="—"
            className="flex-1 rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          {nameDirty && (
            <>
              <button
                type="button"
                onClick={handleNameSave}
                disabled={saving}
                className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => setName(detail.full_name ?? '')}
                disabled={saving}
                className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50 disabled:opacity-60"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-ink-900">Active</div>
          <p className="text-xs text-ink-500">
            Inactive users cannot sign in; existing tokens are revoked.
          </p>
        </div>
        <Toggle
          checked={detail.is_active}
          disabled={saving}
          onChange={handleActiveToggle}
        />
      </div>

      {pendingDeactivate && (
        <ConfirmModal
          heading="Deactivate user?"
          body={<>
            <span className="font-medium text-ink-900">{detail.email ?? detail.id}</span> will
            be blocked from signing in. Existing sessions are revoked. You can
            reactivate at any time.
          </>}
          confirmLabel="Deactivate"
          confirmTone="red"
          pending={saving}
          onConfirm={() => {
            onSave({ is_active: false })
            setPendingDeactivate(false)
          }}
          onCancel={() => setPendingDeactivate(false)}
        />
      )}
    </div>
  )
}

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-60 ${
        checked ? 'bg-brand-500' : 'bg-ink-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  )
}

// =====================================================================
// Subscription form — plan + credits + expires_at + clear-expires
// =====================================================================

function SubscriptionForm({
  detail,
  plans,
  products,
  plansLoading,
  onSave,
  saving,
}: {
  detail: AdminUserDetail
  plans: AdminPlanResponse[]
  products: AdminSubscriptionProductResponse[]
  plansLoading: boolean
  onSave: (patch: AdminUserUpdate) => void
  saving: boolean
}) {
  const sub = detail.subscription
  const [credits, setCredits] = useState(sub.credits_balance.toString())
  const [allowance, setAllowance] = useState(sub.allowance_balance.toString())
  const [expiresAt, setExpiresAt] = useState(isoToDatetimeLocalInput(sub.expires_at))
  const [pendingSub, setPendingSub] = useState<SubscriptionChange | null>(null)
  const [pendingExpireNow, setPendingExpireNow] = useState(false)
  // Only offer "expire now" when there's something to clear (a paid tier, a live
  // allowance, or an expiry date) — a clean free user has nothing to expire.
  const canExpireNow = sub.plan_id !== 1 || sub.allowance_balance > 0 || sub.expires_at !== null

  useEffect(() => { setCredits(sub.credits_balance.toString()) }, [sub.credits_balance])
  useEffect(() => { setAllowance(sub.allowance_balance.toString()) }, [sub.allowance_balance])
  useEffect(() => { setExpiresAt(isoToDatetimeLocalInput(sub.expires_at)) }, [sub.expires_at])

  const creditsValue = Number(credits)
  const creditsValid = Number.isInteger(creditsValue) && creditsValue >= 0
  const creditsDirty = credits !== sub.credits_balance.toString()
  const allowanceValue = Number(allowance)
  const allowanceValid = Number.isInteger(allowanceValue) && allowanceValue >= 0
  const allowanceDirty = allowance !== sub.allowance_balance.toString()
  const expiresDirty = expiresAt !== isoToDatetimeLocalInput(sub.expires_at)

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Subscription — pick by PRODUCT (Free / VIP weekly/monthly/quarterly).
            Picking a SKU sets product_id + derives plan_id; Free nulls the SKU. */}
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-ink-400">Subscription</label>
          <SubscriptionPicker
            productId={sub.product_id}
            planId={sub.plan_id}
            products={products}
            plans={plans}
            onChange={setPendingSub}
            disabled={saving || plansLoading}
          />
          <p className="mt-1 text-xs text-ink-400">
            {sub.unlimited_generations
              ? '✓ Unlimited generations (no token deduction)'
              : 'Tokens are deducted per generation. Free has no SKU.'}
          </p>
        </div>

        {/* Current subscription state — derived/read-only (RC-managed). */}
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-ink-400">Current (saved)</label>
          <div className="rounded-md border border-ink-100 bg-ink-50 px-3 py-2 text-sm">
            {sub.product_id ? (
              <div className="flex flex-wrap items-center gap-2">
                <code className="font-mono text-ink-700">{sub.product_id}</code>
                {sub.product_period && <span className="text-ink-400">· {sub.product_period}</span>}
                {sub.is_trial && (
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">Trial</span>
                )}
              </div>
            ) : (
              <span className="text-ink-400">No product (free / comp)</span>
            )}
          </div>
          <p className="mt-1 text-xs text-ink-400">
            Product / trial are RC-managed; this reflects the saved row.
          </p>
        </div>
      </div>

      {/* Token buckets — two-bucket economy (PX-201) */}
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Allowance (renewable) */}
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-ink-400">Allowance (renewable)</label>
          <div className="flex gap-2">
            <input
              type="number"
              min={0}
              step={1}
              value={allowance}
              onChange={(e) => setAllowance(e.target.value)}
              className="w-32 rounded-md border border-ink-200 px-3 py-2 text-sm tabular-nums focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
            {allowanceDirty && (
              <>
                <button
                  type="button"
                  disabled={!allowanceValid || saving}
                  onClick={() => onSave({ allowance_balance: allowanceValue })}
                  className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setAllowance(sub.allowance_balance.toString())}
                  className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
          {allowanceDirty && !allowanceValid && (
            <p className="text-xs text-red-600">Must be a non-negative integer.</p>
          )}
          <p className="text-xs text-amber-700">
            Burns at period end. The next RC renewal resets it to the SKU's allowance — this is a live grant only.
          </p>
        </div>

        {/* Purchased (never expires) */}
        <div className="flex flex-col gap-1">
          <label className="text-xs uppercase tracking-wide text-ink-400">Purchased (never expires)</label>
          <div className="flex gap-2">
            <input
              type="number"
              min={0}
              step={1}
              value={credits}
              onChange={(e) => setCredits(e.target.value)}
              className="w-32 rounded-md border border-ink-200 px-3 py-2 text-sm tabular-nums focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
            {creditsDirty && (
              <>
                <button
                  type="button"
                  disabled={!creditsValid || saving}
                  onClick={() => onSave({ credits_balance: creditsValue })}
                  className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setCredits(sub.credits_balance.toString())}
                  className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
          {creditsDirty && !creditsValid && (
            <p className="text-xs text-red-600">Must be a non-negative integer.</p>
          )}
          <p className="text-xs text-ink-400">Token packs + signup bonus. Spent after allowance.</p>
        </div>
      </div>

      {/* expires_at */}
      <div className="flex flex-col gap-1">
        <label className="text-xs uppercase tracking-wide text-ink-400">
          Subscription expires at
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="rounded-md border border-ink-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          {expiresDirty && (
            <>
              <button
                type="button"
                disabled={saving}
                onClick={() => onSave({ expires_at: datetimeLocalInputToISO(expiresAt) })}
                className="rounded-md bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:opacity-60"
              >
                Save date
              </button>
              <button
                type="button"
                onClick={() => setExpiresAt(isoToDatetimeLocalInput(sub.expires_at))}
                className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50"
              >
                Cancel
              </button>
            </>
          )}
          {sub.expires_at && (
            <button
              type="button"
              disabled={saving}
              onClick={() => onSave({ unset_expires_at: true })}
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 transition hover:bg-amber-100 disabled:opacity-60"
            >
              Clear (lifetime)
            </button>
          )}
        </div>
        <p className="text-xs text-ink-400">
          {sub.expires_at
            ? `Currently expires ${formatDateTime(sub.expires_at)}.`
            : 'No expiry set — counts as lifetime / comp.'}
          {sub.cancelled_at && (
            <> · Cancelled {formatDateTime(sub.cancelled_at)}.</>
          )}
          {sub.is_expired && (
            <span className="text-amber-700"> · Lapsed — effectively Free (the row settles via the PX-206 job / reconcile cron).</span>
          )}
        </p>

        {canExpireNow && (
          <div className="border-t border-ink-100 pt-3">
            <button
              type="button"
              disabled={saving}
              onClick={() => setPendingExpireNow(true)}
              className="rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 transition hover:bg-red-100 disabled:opacity-60"
            >
              Expire now
            </button>
            <p className="mt-1 text-xs text-ink-400">
              Downgrade to Free immediately — zeroes the renewable allowance, keeps purchased tokens. For support / testing.
            </p>
          </div>
        )}
      </div>

      {pendingExpireNow && (
        <ConfirmModal
          heading="Expire subscription now?"
          body={<>
            Immediately downgrades <strong>{sub.plan_name}</strong> to <strong>Free</strong> and
            zeroes the renewable allowance ({sub.allowance_balance} tokens). Purchased tokens
            ({sub.credits_balance}) are kept. This mirrors a real expiry and can't be undone except
            by re-granting a subscription.
          </>}
          confirmLabel="Expire now"
          confirmTone="red"
          pending={saving}
          onConfirm={() => {
            onSave({ expire_now: true })
            setPendingExpireNow(false)
          }}
          onCancel={() => setPendingExpireNow(false)}
        />
      )}

      {pendingSub !== null && (
        <ConfirmModal
          heading="Change subscription?"
          body={<>
            Switching to <strong>{pendingSub.label}</strong>.
            {pendingSub.patch.unset_product_id
              ? <> Free has no SKU — the product is cleared.</>
              : <> The plan tier is derived from the product.</>}
            {' '}<code className="font-mono">expires_at</code> and token balances
            stay as-is — edit them separately.
          </>}
          confirmLabel="Change subscription"
          confirmTone="brand"
          pending={saving}
          onConfirm={() => {
            onSave(pendingSub.patch)
            setPendingSub(null)
          }}
          onCancel={() => setPendingSub(null)}
        />
      )}
    </div>
  )
}

// =====================================================================
// Danger zone
// =====================================================================

function DangerZone({
  email,
  pending,
  onConfirm,
}: {
  email: string
  pending: boolean
  onConfirm: () => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [confirming, setConfirming] = useState(false)
  return (
    <section className="rounded-xl border border-red-200 bg-red-50/50 p-5">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="text-sm font-semibold uppercase tracking-wide text-red-700"
      >
        Danger zone {expanded ? '▾' : '▸'}
      </button>
      {expanded && (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-ink-700">
            <strong>Soft-delete</strong> marks <code className="font-mono">deleted_at</code>{' '}
            on the row. The user can restore by signing back in within a
            30-day grace window; after that the daily cron hard-deletes
            them with cascade.
          </p>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={pending}
            className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-60"
          >
            Soft delete user
          </button>
        </div>
      )}

      {confirming && (
        <ConfirmModal
          heading="Soft delete user?"
          body={<>
            <span className="font-medium text-ink-900">{email}</span> will be
            marked deleted and can restore within 30 days by signing in. After
            that, all their generations / subscriptions / tokens cascade-delete.
          </>}
          confirmLabel="Soft delete"
          confirmTone="red"
          pending={pending}
          onConfirm={() => {
            onConfirm()
            setConfirming(false)
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </section>
  )
}

// =====================================================================
// Generic confirm modal — used by deactivate / plan-change / soft-delete.
// (The Users-list page has its own PlanChangeConfirmModal with extra
// copy specific to row-context; this one is the smaller variant for
// the detail page where the surrounding form already shows context.)
// =====================================================================

function ConfirmModal({
  heading,
  body,
  confirmLabel,
  confirmTone,
  pending,
  onConfirm,
  onCancel,
}: {
  heading: string
  body: React.ReactNode
  confirmLabel: string
  confirmTone: 'brand' | 'red'
  pending: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const confirmClass =
    confirmTone === 'red'
      ? 'bg-red-500 hover:bg-red-600'
      : 'bg-brand-500 hover:bg-brand-600'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold tracking-tight">{heading}</h2>
        <p className="mt-2 text-sm text-ink-600">{body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="rounded-md border border-ink-200 px-3 py-1.5 text-sm font-medium text-ink-700 transition hover:bg-ink-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold text-white transition disabled:opacity-60 ${confirmClass}`}
          >
            {pending ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
